import React, { useEffect, useRef, useState } from 'react'
import HorizontalScroller from '../components/molecules/horizontal-scrollbar/horizontal-scrollbar'
import CustomTab, { Tabs } from '../components/molecules/custom-tab/custom-tab'
import CustomTable, { TableColumn } from '../components/molecules/table/table'
import { scrollToOffset } from '../utils/helper-functions'
import {
  calcClientPagination,
  CapsuleTitleInfo,
  generateBusinessUsersRowsAndCols,
  generateCols,
  mapBusinessData,
  mapFbPagesData,
  ToolTipTextOtherAccounts,
} from './utils'
import { BUSINESS_DETAILS } from '../constants/routes'
import { useAuth } from '../customHooks/useAuth'
import axios from '../utils/axios'
import { BoardedTableData, CanBeAnything } from '../types'
import { CapsuleInfo } from '../pages/business-accounts/diagnostics'
import { LIMIT_OPTIONS } from '../constants/constants'
import { t } from '../utils/i18n'
import { Capsule } from '../components/molecules/capsule/capsule'
import { InputField } from '../components/molecules/input/input-field'
import { ReactComponent as SearchIcon } from '../assets/svgs/search-icon.svg'
import { InfoTooltip } from '../components/atoms/info-tooltip/info-tooltip'
import { AssetDrawerContainer } from './asset-drawer-container'
import { RowDataType } from 'rsuite/esm/Table'
import { ReactComponent as Download } from '../assets/svgs/download.svg'

export enum ACCOUNT_TYPE {
  ONBOARDED = 'onboarded',
  OTHER = 'other',
}

interface BusinessAccountsContainerProps {
  businessId: string
  capsuleInfo: CapsuleInfo[]
  callMetaAPIOnParent?: () => void
}

const RECORDS_PER_PAGE = process.env.REACT_APP_PAGINATION_LIMIT as string

export const BusinessAccountsContainer = ({
  businessId,
  capsuleInfo,
  callMetaAPIOnParent,
}: BusinessAccountsContainerProps) => {
  const { user } = useAuth()
  const [onBoardedList, setonBoardedList] = useState<BoardedTableData[]>([])
  const [notBoardedList, setNotBoardedList] = useState<BoardedTableData[]>([])
  const [businessUsersList, setBusinessUsersList] = useState<[]>([])
  const [cols, setCols] = useState<TableColumn[]>()
  const [rows, setRows] = useState<BoardedTableData[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedAsset, setSelectedAsset] = useState<RowDataType>({})
  const [selectedCapsule, setSelectedCapsule] = useState<number>(1)

  const getOtherTitleText = () => {
    let str = ''
    if (selectedCapsule === 4) {
      str = `Other pages in Business (${notBoardedList?.length ?? 0})`
    } else if (selectedCapsule === 6) {
      str = `Other datasets in Business (${notBoardedList?.length ?? 0})`
    } else if (selectedCapsule === 7) {
      str = `Other catalogs in Business (${notBoardedList?.length ?? 0})`
    } else {
      str = `Other accounts in Business (${notBoardedList?.length ?? 0})`
    }
    return str
  }

  const tabOptions: Tabs[] = [
    {
      id: 1,
      title: `Onboarded (${capsuleInfo.find((capsule) => capsule.id === selectedCapsule)?.count ?? 0})`,
    },
    {
      id: 2,
      title: getOtherTitleText(),
    },
  ]
  const [activeTab, setActiveTab] = useState(tabOptions[0]?.id)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(Number(RECORDS_PER_PAGE))
  const [totalRecords, setTotalRecords] = useState(0)

  const tableRef = useRef<CanBeAnything>(null)

  const stringifiedCapsuleInfo = capsuleInfo ? JSON.stringify(capsuleInfo) : ''

  useEffect(() => {
    if (activeTab === 1) {
      const selectedBusinessCapsule = capsuleInfo.find((capsule) => capsule.id === selectedCapsule)

      const { tableCols, tableRowData } = generateCols(
        onBoardedList,
        activeTab,
        selectedBusinessCapsule as CapsuleInfo
      )
      setCols(tableCols)
      setRows(tableRowData)
    }
  }, [onBoardedList])

  useEffect(() => {
    const selectedBusinessCapsule = capsuleInfo.find((capsule) => capsule.id === selectedCapsule)

    if (activeTab === 2) {
      const { tableCols, tableRowData } = generateCols(
        notBoardedList,
        activeTab,
        selectedBusinessCapsule as CapsuleInfo
      )
      setCols(tableCols)
      setRows(tableRowData)
    }
  }, [notBoardedList])

  useEffect(() => {
    const selectedBusinessCapsule = capsuleInfo.find((capsule) => capsule.id === selectedCapsule)
    if (
      businessUsersList?.length &&
      selectedBusinessCapsule?.title === CapsuleTitleInfo.BUSINESS_USERS
    ) {
      const { tableCols, tableRow } = generateBusinessUsersRowsAndCols(businessUsersList)
      setCols(tableCols)
      setRows(tableRow)
    }
  }, [businessUsersList])

  useEffect(() => {
    if (selectedCapsule && capsuleInfo?.length) {
      getCapsuleSelectedData(selectedCapsule)
    }
    if (activeTab !== 1) {
      setActiveTab(1)
    }
  }, [selectedCapsule, stringifiedCapsuleInfo])

  useEffect(() => {
    const selectedBusinessCapsule = capsuleInfo?.find((capsule) => capsule.id === selectedCapsule)
    if (selectedBusinessCapsule?.title === CapsuleTitleInfo.BUSINESS_USERS) {
      getCapsuleSelectedData(selectedCapsule, true)
    } else {
      if (activeTab === 1) {
        getCapsuleSelectedData(selectedCapsule, true)
      } else {
        const { rows, cols } = calcClientPagination(
          notBoardedList,
          limit,
          page,
          searchTerm,
          activeTab,
          selectedBusinessCapsule as CapsuleInfo
        )
        setCols(cols)
        setRows(rows)
      }
    }
  }, [limit, page, searchTerm, activeTab])

  const onCapsuleSelected = (id: number) => {
    setSelectedCapsule(id)
    setPage(1)
    scrollToOffset(tableRef.current, 400)
  }

  const handleSearch = (term: string) => {
    setSearchTerm(term)
  }

  const getCapsuleSelectedData = async (selectedCapsuleId: number, withQuery = false) => {
    const selectedBusinessCapsule = capsuleInfo.find((capsule) => capsule.id === selectedCapsuleId)

    if (
      selectedBusinessCapsule &&
      selectedBusinessCapsule.title === CapsuleTitleInfo.OWNED_ACCOUNTS
    ) {
      if (!withQuery) {
        const ownedOnboardedAccountsData = await callAccountAPI(
          'owned',
          ACCOUNT_TYPE.ONBOARDED,
          businessId
        )
        const notOnBoardedAccountsData = await callAccountAPI(
          'owned',
          ACCOUNT_TYPE.OTHER,
          businessId
        )
        setonBoardedList(mapBusinessData(ownedOnboardedAccountsData?.onboardedOwnedAdAccounts))
        setNotBoardedList(mapBusinessData(notOnBoardedAccountsData))
      } else {
        const data = await callAccountAPI(
          'owned',
          activeTab === 1 ? ACCOUNT_TYPE.ONBOARDED : ACCOUNT_TYPE.OTHER,
          businessId
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        activeTab === 1
          ? setonBoardedList(mapBusinessData(data?.onboardedOwnedAdAccounts))
          : setNotBoardedList(mapBusinessData(data))
      }
    } else if (
      selectedBusinessCapsule &&
      selectedBusinessCapsule.title === CapsuleTitleInfo.CLIENT_ACCOUNTS
    ) {
      if (!withQuery) {
        const clientOnboardedAccountsData = await callAccountAPI(
          'client',
          ACCOUNT_TYPE.ONBOARDED,
          businessId
        )
        const notOnBoardedAccountsData = await callAccountAPI(
          'client',
          ACCOUNT_TYPE.OTHER,
          businessId
        )
        setNotBoardedList(mapBusinessData(notOnBoardedAccountsData))
        setonBoardedList(mapBusinessData(clientOnboardedAccountsData?.onboardedClientAdAccounts))
      } else {
        const data = await callAccountAPI(
          'client',
          activeTab === 1 ? ACCOUNT_TYPE.ONBOARDED : ACCOUNT_TYPE.OTHER,
          businessId
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        activeTab === 1
          ? setonBoardedList(mapBusinessData(data?.onboardedClientAdAccounts))
          : setNotBoardedList(mapBusinessData(data))
      }
    } else if (
      selectedBusinessCapsule &&
      selectedBusinessCapsule.title === CapsuleTitleInfo.BUSINESS_USERS
    ) {
      if (!withQuery) {
        const businessUsers = await callBusinessUsersAPI()
        setBusinessUsersList(businessUsers)
      } else {
        const businessUsers = await callBusinessUsersAPI(true)
        setBusinessUsersList(businessUsers)
      }
    } else if (
      selectedBusinessCapsule &&
      selectedBusinessCapsule.title === CapsuleTitleInfo.FACEBOOK_PAGES
    ) {
      if (!withQuery) {
        const facebookPagesDataOnBoarded = await callAccountAPI(
          'pages',
          ACCOUNT_TYPE.ONBOARDED,
          businessId
        )
        const facebookPagesDataOther = await callAccountAPI('pages', ACCOUNT_TYPE.OTHER, businessId)

        setonBoardedList(mapFbPagesData(facebookPagesDataOnBoarded.onboardedPages))
        setNotBoardedList(mapFbPagesData(facebookPagesDataOther))
      } else {
        const data = await callAccountAPI(
          'pages',
          activeTab === 1 ? ACCOUNT_TYPE.ONBOARDED : ACCOUNT_TYPE.OTHER,
          businessId
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        activeTab === 1
          ? setonBoardedList(mapFbPagesData(data?.onboardedPages))
          : setNotBoardedList(mapBusinessData(data))
      }
    } else if (
      selectedBusinessCapsule &&
      selectedBusinessCapsule.title === CapsuleTitleInfo.INSTAGRAM_ACCOUNTS
    ) {
      if (!withQuery) {
        const instaOnboardedAccounts = await callAccountAPI(
          'insta',
          ACCOUNT_TYPE.ONBOARDED,
          businessId
        )
        const instaOtherAccounts = await callAccountAPI('insta', ACCOUNT_TYPE.OTHER, businessId)

        setonBoardedList(mapFbPagesData(instaOnboardedAccounts.onboardedInstaAccounts))
        setNotBoardedList(mapFbPagesData(instaOtherAccounts))
      } else {
        const data = await callAccountAPI(
          'insta',
          activeTab === 1 ? ACCOUNT_TYPE.ONBOARDED : ACCOUNT_TYPE.OTHER,
          businessId
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        activeTab === 1
          ? setonBoardedList(mapFbPagesData(data?.onboardedInstaAccounts))
          : setNotBoardedList(mapBusinessData(data))
      }
    } else if (
      selectedBusinessCapsule &&
      selectedBusinessCapsule.title === CapsuleTitleInfo.DATASET
    ) {
      if (!withQuery) {
        const datasetOnboardedAccounts = await callAccountAPI(
          'dataset',
          ACCOUNT_TYPE.ONBOARDED,
          businessId
        )
        const datasetOtherAccounts = await callAccountAPI('dataset', ACCOUNT_TYPE.OTHER, businessId)

        setonBoardedList(mapFbPagesData(datasetOnboardedAccounts.onboardedDataset))
        setNotBoardedList(mapFbPagesData(datasetOtherAccounts))
      } else {
        const data = await callAccountAPI(
          'dataset',
          activeTab === 1 ? ACCOUNT_TYPE.ONBOARDED : ACCOUNT_TYPE.OTHER,
          businessId
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        activeTab === 1
          ? setonBoardedList(mapFbPagesData(data?.onboardedDataset))
          : setNotBoardedList(mapBusinessData(data))
      }
    } else if (
      selectedBusinessCapsule &&
      selectedBusinessCapsule.title === CapsuleTitleInfo.CATALOGUE
    ) {
      if (!withQuery) {
        const catalogsOnboardedAccounts = await callAccountAPI(
          'catalogs',
          ACCOUNT_TYPE.ONBOARDED,
          businessId
        )
        const catalogsOtherAccounts = await callAccountAPI(
          'catalogs',
          ACCOUNT_TYPE.OTHER,
          businessId
        )

        setonBoardedList(mapFbPagesData(catalogsOnboardedAccounts.onboardedCatalogs))
        setNotBoardedList(mapFbPagesData(catalogsOtherAccounts))
      } else {
        const data = await callAccountAPI(
          'catalogs',
          activeTab === 1 ? ACCOUNT_TYPE.ONBOARDED : ACCOUNT_TYPE.OTHER,
          businessId
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        activeTab === 1
          ? setonBoardedList(mapFbPagesData(data?.onboardedCatalogs))
          : setNotBoardedList(mapBusinessData(data))
      }
    }
  }

  const callAccountAPI = async (parent: string, type: ACCOUNT_TYPE, businessId: string) => {
    try {
      setIsLoading(true)
      const config = {
        method: 'GET',
        url: !searchTerm
          ? `${BUSINESS_DETAILS}/${businessId}/${parent}/${type}/list/?recordsPerPage=${limit}&page=${page}`
          : `${BUSINESS_DETAILS}/${businessId}/${parent}/${type}/list/?recordsPerPage=${limit}&page=${page}&searchFields=account_name,account_id&searchText=${searchTerm}`,
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }

      const accounts = await axios(config)
      return accounts?.data?.data
    } catch {
      setIsLoading(false)
    } finally {
      setIsLoading(false)
    }
  }

  const callBusinessUsersAPI = async (withQuery = false) => {
    try {
      setIsLoading(true)
      const config = {
        method: 'GET',
        url: !withQuery
          ? `${BUSINESS_DETAILS}/${businessId}/users`
          : `${BUSINESS_DETAILS}/${businessId}/users?recordsPerPage=${limit}&page=${page}&searchFields=email,name&searchText=${searchTerm}`,
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : { 'x-user': user?.email, 'x-access-token': user?.tokens?.accessToken?.token },
      }
      const users = await axios(config)
      setTotalRecords(users?.data?.data?.pagination?.totalRecords)
      return users?.data?.data?.businessUsers
    } catch {
      setIsLoading(false)
    } finally {
      setIsLoading(false)
    }
  }
  const getTotalRecords = () => {
    const selectedBusinessCapsule = capsuleInfo.find((capsule) => capsule.id === selectedCapsule)
    if (selectedBusinessCapsule?.title === CapsuleTitleInfo.BUSINESS_USERS) {
      return totalRecords
    } else {
      return activeTab === 1
        ? selectedBusinessCapsule?.count
        : searchTerm
          ? rows.length
          : notBoardedList?.length
    }
  }

  const onViewCTAClicked = (key: string, rowData: RowDataType) => {
    if (key === 'cta') {
      setSelectedAsset(rowData)
    }
  }
  const callDownloadAPI = async () => {
    try {
      const config: CanBeAnything = {
        method: 'GET',
        responseType: 'arraybuffer',
        url: `${BUSINESS_DETAILS}/${businessId}/users/download`,
        headers: user?.isSSO
          ? {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
              'x-source': 'sso',
            }
          : {
              'x-user': user?.email,
              'x-access-token': user?.tokens?.accessToken?.token,
            },
      }
      const response = await axios(config)
      const addRes = response.data
      const outputFilename = `users.xlsx`
      const url = URL.createObjectURL(new Blob([addRes]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', outputFilename)
      document.body.appendChild(link)
      link.click()
    } catch (error) {
      console.log(error)
    }
  }

  const onDownloadHandler = () => {
    callDownloadAPI()
  }

  const callAccountAPIAfterDeboard = () => {
    getCapsuleSelectedData(selectedCapsule)
    callMetaAPIOnParent?.()
  }
  return (
    <div className="business-accounts-container">
      <h2 style={{ marginBottom: '0.75rem' }}>{t('assets.diagnostics.heading')}</h2>
      <HorizontalScroller style={{ width: '71rem' }} scrollAmount={300}>
        <div className={`capsule-data ${isLoading ? 'no-cursor' : ''}`}>
          {capsuleInfo?.map(({ title, id, subInfo, toolTipContent }) => {
            return (
              <Capsule
                id={id}
                title={title}
                subInfo={subInfo}
                isTooltip
                key={id}
                onCapsuleClicked={onCapsuleSelected}
                selectedCapsuleId={selectedCapsule}
                toolTipInfo={toolTipContent}
              />
            )
          })}
        </div>
      </HorizontalScroller>
      <div
        className={`business-accounts-tab ${selectedCapsule === 3 ? 'align-right' : ''} ${isLoading ? 'no-cursor' : ''} `}
      >
        <div className="tab-tooltip-container">
          {selectedCapsule !== 3 && (
            <CustomTab tabs={tabOptions} activeTab={activeTab} handleTabChange={setActiveTab} />
          )}
          {activeTab === 2 && <InfoTooltip tooltipContent={ToolTipTextOtherAccounts} />}
        </div>

        <div className="input-download-btn-container">
          <InputField
            fieldType="text"
            label=""
            value={searchTerm}
            onChange={(s) => handleSearch(s)}
            placeholder={t('search')}
            icon={<SearchIcon />}
          />
          {selectedCapsule === 3 ? (
            <div className="download-icon">
              <Download onClick={onDownloadHandler} />
            </div>
          ) : null}
        </div>
      </div>
      <div ref={tableRef} style={{ marginBottom: '2rem' }}>
        <CustomTable
          data={rows}
          columns={cols}
          isLoading={isLoading}
          isPaginated={true}
          total={getTotalRecords()}
          currentPage={page}
          pageRange={LIMIT_OPTIONS}
          onPageChange={setPage}
          recordsPerPage={limit}
          onLimitChange={(newLimit: number | null) => {
            setLimit(Number(newLimit))
            setPage(1)
          }}
          onCellClicked={onViewCTAClicked}
        />
      </div>
      <div>
        <AssetDrawerContainer
          selectedCapsuleInfo={
            capsuleInfo?.find((capsule) => capsule.id === selectedCapsule) as CapsuleInfo
          }
          selectedAsset={selectedAsset}
          businessId={businessId}
          onDrawerClose={() => setSelectedAsset({})}
          callAccountAPIonParent={callAccountAPIAfterDeboard}
        />
      </div>
    </div>
  )
}
